import {
  getCurrentUser,
  signOut as amplifySignOut,
  AuthUser,
  fetchAuthSession,
  signInWithRedirect,
} from 'aws-amplify/auth';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  Context,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchUserProfileById } from '../api/fetchers.ts';
import type { PublicProfileResponse } from '@mrb91002/shared-types';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  error: string | null;
  accessToken: string | null;
  idToken: string | null;
  session: Record<string, unknown> | null;
  screen_name: string | null;
  setScreenName: (sn: string | null) => void;
  profile: PublicProfileResponse | null;
  setProfile: (profile: PublicProfileResponse | null) => void;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  getAccessToken: () => Promise<string | null>;
  getCurrentSession: () => Promise<Record<string, unknown> | null>;
  reloadProfile: () => Promise<void>;
}

const AuthContext: Context<AuthContextType | undefined> = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIDToken] = useState<string | null>(null);
  const [session, setSession] = useState<Record<string, unknown> | null>(null);

  const [screen_name, setScreenName] = useState<string | null>(null);
  const [profile, setProfile] = useState<PublicProfileResponse | null>(null);



  // Function declarations should be moved to the top
  const getCurrentSession = useCallback(async (): Promise<Record<string, unknown> | null> => {
    try {
      const session = await fetchAuthSession();
      return session as Record<string, unknown>;
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      }
      return null;
    }
  }, []);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      }
      return null;
    }
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      }
      return null;
    }
  }, []);

  const signIn = useCallback(async (): Promise<void> => {
    try {
      console.log('🔐 [AUTH DEBUG] Starting sign in from origin:', window.location.origin);
      console.log('🔐 [AUTH DEBUG] Current URL:', window.location.href);
      await signInWithRedirect();
    } catch (error: unknown) {
      console.error('🔴 [AUTH ERROR] Sign in failed:', error);
      if (error instanceof Error) {
        console.error('🔴 [AUTH ERROR] Error message:', error.message);
        console.error('🔴 [AUTH ERROR] Error stack:', error.stack);
        setError(error.message);
      }
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    // Clear all local state
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
    setAccessToken(null);
    setIDToken(null);
    setSession(null);
    setScreenName(null);
    setProfile(null);
    localStorage.removeItem('fpw_screen_name');
    
    // Clear Cognito tokens from localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('CognitoIdentityServiceProvider')) {
        localStorage.removeItem(key);
      }
    });
    
    // Force full logout by redirecting to Cognito logout endpoint
    const domain = 'dev-mbenav-pattern-warehouse-v3.auth.us-west-2.amazoncognito.com';
    const clientId = '70105j8cfp0i4pmvqbpbh9013m';
    const logoutUri = encodeURIComponent(window.location.origin);
    const logoutUrl = `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
    
    window.location.href = logoutUrl;
  }, []);

  // On mount: load user and profile
  useEffect(() => {


    const initAuth = async () => {
      try {
        console.log('🔐 [AUTH DEBUG] Starting auth initialization...');
        
        // First check if we have valid tokens
        const session = await fetchAuthSession();
        console.log('🔐 [AUTH DEBUG] Session check:', { 
          hasTokens: !!session.tokens,
          hasIdToken: !!session.tokens?.idToken,
          hasAccessToken: !!session.tokens?.accessToken
        });
        
        if (!session.tokens?.idToken || !session.tokens?.accessToken) {
          console.log('🔴 [AUTH DEBUG] No valid tokens found - user not authenticated');
          throw new Error('No valid tokens');
        }
        
        const currentUser = await getCurrentUser();
        console.log('🔐 [AUTH DEBUG] Current user object:', currentUser);
        console.log('🔐 [AUTH DEBUG] User ID for manual lookup:', currentUser.userId);
        
        // Validate that we have a proper user with userId
        if (!currentUser.userId) {
          console.log('🔴 [AUTH DEBUG] Invalid user object - no userId');
          throw new Error('Invalid user - no userId');
        }
        
        console.log('🔐 [AUTH DEBUG] Setting user and authenticated state');
        setUser(currentUser);
        setIsAuthenticated(true);
        setError(null);



        // Skip profile fetching on create profile page
        if (window.location.pathname === '/create/profile') {
          console.log('🔐 [AUTH DEBUG] On create profile page, skipping profile fetch');
          return;
        }

        // Skip profile fetching on login redirect page
        if (window.location.pathname === '/login-redirect') {
          console.log('🔐 [AUTH DEBUG] On login redirect page, skipping profile fetch');
          return;
        }

        // Try to get screen name from localStorage first (fallback for CORS issues)
        const cachedScreenName = localStorage.getItem('fpw_screen_name');
        if (cachedScreenName) {
          console.log('🔐 [AUTH DEBUG] Using cached screen name:', cachedScreenName);
          setScreenName(cachedScreenName);
        }

        // Try to fetch profile
        let loadedProfile: PublicProfileResponse | null = null;
        try {
          if (currentUser.userId) {
            console.log('🔐 [AUTH DEBUG] Fetching profile for user ID:', currentUser.userId);
            loadedProfile = await fetchUserProfileById(currentUser.userId);
            setProfile(loadedProfile);
            if (loadedProfile?.screen_name) {
              setScreenName(loadedProfile.screen_name);
              // Cache screen name for future use
              localStorage.setItem('fpw_screen_name', loadedProfile.screen_name);
              console.log('🔐 [AUTH DEBUG] Loaded and cached profile:', loadedProfile);
            }
          }
        } catch (err) {
          console.error('🔴 [AUTH ERROR] Profile fetch failed:', err);
          console.log('🔐 [AUTH DEBUG] Using cached screen name if available:', cachedScreenName);
          // Keep cached screen name if profile fetch fails
          setProfile(null);
          if (!cachedScreenName) {
            setScreenName(null);
            // If authenticated user has no profile and not on create profile page, redirect there
            if (window.location.pathname !== '/create/profile' && window.location.pathname !== '/login-redirect') {
              console.log('🔄 [AUTH] Authenticated user without profile, redirecting to create profile');
              navigate('/create/profile');
            }
          }
        }
      } catch (error: unknown) {
        console.log('🔴 [AUTH DEBUG] Auth initialization failed:', error);
        
        // Check if this is a token expiration vs not logged in
        if (error instanceof Error && (error.message.includes('token') || error.message.includes('expired'))) {
          console.log('🔴 [AUTH DEBUG] Token expired - clearing auth state');
          // Clear any stored auth state
          localStorage.removeItem('fpw_screen_name');
          // Clear corrupted Cognito tokens
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('CognitoIdentityServiceProvider')) {
              localStorage.removeItem(key);
            }
          });
        } else {
          console.log('🔴 [AUTH DEBUG] User not authenticated');
        }
        
        setIsAuthenticated(false);
        setUser(null);
        setProfile(null);
        setScreenName(null);
        if (error instanceof Error) {
          setError(error.message);
        }
      } finally {
        console.log('🔐 [AUTH DEBUG] Auth initialization complete, isLoading -> false');
        setIsLoading(false);
      }
    };

    initAuth();
  }, [getCurrentSession]);  // Use getCurrentSession here

  useEffect(() => {
    const fetchSessionData = async (): Promise<void> => {
      const token = await fetchAuthSession();
      const sess = await getCurrentSession();

      setIDToken(token.tokens?.idToken?.toString() || null);
      setAccessToken(token.tokens?.accessToken?.toString() || null);
      setSession(sess);
    };

    if (isAuthenticated) {
      fetchSessionData();
    }
  }, [isAuthenticated, getCurrentSession]);  // Add getCurrentSession to the dependency array

  const reloadProfile = useCallback(async () => {
    if (!user) return;
    try {
      const loadedProfile = await fetchUserProfileById(user.userId);
      setProfile(loadedProfile);
      setScreenName(loadedProfile?.screen_name || null);
    } catch {
      setProfile(null);
      setScreenName(null);
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        error,
        accessToken,
        idToken,
        session,
        screen_name,
        setScreenName,
        profile,
        setProfile,
        signIn,
        signOut,
        getIdToken,
        getAccessToken,
        getCurrentSession,
        reloadProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
